import { useEffect, useState } from "react";
import { Plus, Shield, Pencil, Clock, DollarSign, Calendar } from "lucide-react";
import api from "@/lib/api";

const roleLabels = { admin: "Admin", operator: "Operador", finance: "Financeiro", support: "Suporte" };
const roleBadge = { admin: "crm-badge crm-badge-red", operator: "crm-badge crm-badge-blue", finance: "crm-badge crm-badge-green", support: "crm-badge crm-badge-purple" };
const DAY_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export default function OperatorList() {
  const [operators, setOperators] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [configId, setConfigId] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "operator" });
  const [salaryForm, setSalaryForm] = useState({ base_salary: "", hourly_rate: "", bonus_type: "", bonus_value: "", work_start: "10:00", work_end: "19:00", work_days: [1, 2, 3, 4, 5] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchOps = () => api.get("/operators").then((r) => setOperators(r.data)).catch(() => {});
  useEffect(() => { fetchOps(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      if (editId) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/operators/${editId}`, payload);
      } else {
        await api.post("/operators", form);
      }
      setShowForm(false); setEditId(null); setForm({ name: "", email: "", password: "", role: "operator" });
      fetchOps();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Erro ao salvar operador.");
    }
    setSaving(false);
  };

  const handleSalarySubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      await api.put(`/operators/${configId}/salary`, {
        base_salary: salaryForm.base_salary ? Number(salaryForm.base_salary) : null,
        hourly_rate: salaryForm.hourly_rate ? Number(salaryForm.hourly_rate) : null,
        bonus_type: salaryForm.bonus_type || null,
        bonus_value: salaryForm.bonus_value ? Number(salaryForm.bonus_value) : null,
        work_start: salaryForm.work_start || null,
        work_end: salaryForm.work_end || null,
        work_days: salaryForm.work_days,
      });
      setConfigId(null);
      fetchOps();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Erro ao salvar configuração.");
    }
    setSaving(false);
  };

  const startEdit = (op) => {
    setForm({ name: op.name, email: op.email, password: "", role: op.role });
    setEditId(op.id); setShowForm(true); setConfigId(null);
  };

  const startConfig = (op) => {
    setSalaryForm({
      base_salary: op.base_salary || "",
      hourly_rate: op.hourly_rate || "",
      bonus_type: op.bonus_type || "",
      bonus_value: op.bonus_value || "",
      work_start: op.work_start || "10:00",
      work_end: op.work_end || "19:00",
      work_days: op.work_days || [1, 2, 3, 4, 5],
    });
    setConfigId(op.id); setShowForm(false); setEditId(null);
  };

  const toggleDay = (day) => {
    setSalaryForm((prev) => ({
      ...prev,
      work_days: prev.work_days.includes(day)
        ? prev.work_days.filter((d) => d !== day)
        : [...prev.work_days, day].sort(),
    }));
  };

  const toggleStatus = async (op) => {
    await api.put(`/operators/${op.id}`, { status: op.status === "active" ? "inactive" : "active" });
    fetchOps();
  };

  return (
    <div data-testid="operator-list-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="crm-heading">Operadores</h1>
        <button onClick={() => { setShowForm(!showForm); setEditId(null); setConfigId(null); setForm({ name: "", email: "", password: "", role: "operator" }); }} className="crm-btn-primary" data-testid="add-operator-btn">
          <Plus className="w-4 h-4" /> Novo Operador
        </button>
      </div>

      {/* Create/Edit form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="crm-card p-6 mb-6 space-y-4" data-testid="operator-form">
          <h3 className="crm-subheading">{editId ? "Editar Operador" : "Novo Operador"}</h3>
          {error && <div className="p-3 rounded-xl text-sm font-medium" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#ef4444" }}>{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="crm-label">Nome *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="crm-input" data-testid="operator-name" /></div>
            <div><label className="crm-label">Email *</label><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="crm-input" data-testid="operator-email" /></div>
            <div><label className="crm-label">{editId ? "Nova Senha (vazio = manter)" : "Senha *"}</label><input type="password" required={!editId} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="crm-input" data-testid="operator-password" /></div>
            <div><label className="crm-label">Cargo</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="crm-input" data-testid="operator-role">
                <option value="admin">Admin</option><option value="operator">Operador</option><option value="finance">Financeiro</option><option value="support">Suporte</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="crm-btn-primary text-sm">{saving ? "Salvando..." : editId ? "Atualizar" : "Criar"}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditId(null); }} className="crm-btn-secondary text-sm">Cancelar</button>
          </div>
        </form>
      )}

      {/* Salary/Work config form */}
      {configId && (
        <form onSubmit={handleSalarySubmit} className="crm-card overflow-hidden mb-6" data-testid="salary-config-form">
          <div className="px-6 py-4 flex items-center gap-2" style={{ borderBottom: "1px solid var(--crm-border)", background: "var(--crm-input-bg)" }}>
            <DollarSign className="w-4 h-4" style={{ color: "var(--crm-accent)" }} />
            <h3 className="text-sm font-bold" style={{ color: "var(--crm-text)" }}>
              Configuração — {operators.find((o) => o.id === configId)?.name}
            </h3>
          </div>

          <div className="p-6 space-y-6">
            {/* Financial */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: "var(--crm-text-muted)" }}>
                <DollarSign className="w-3 h-3 inline mr-1" />Dados Financeiros
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="crm-label">Salário Base (¥/mês)</label>
                  <input type="number" min="0" value={salaryForm.base_salary} onChange={(e) => setSalaryForm({ ...salaryForm, base_salary: e.target.value })}
                    className="crm-input" placeholder="250000" data-testid="salary-base" />
                </div>
                <div>
                  <label className="crm-label">Valor Hora (¥/hora)</label>
                  <input type="number" min="0" value={salaryForm.hourly_rate} onChange={(e) => setSalaryForm({ ...salaryForm, hourly_rate: e.target.value })}
                    className="crm-input" placeholder="1500" data-testid="salary-hourly" />
                </div>
                <div>
                  <label className="crm-label">Tipo de Bônus</label>
                  <select value={salaryForm.bonus_type} onChange={(e) => setSalaryForm({ ...salaryForm, bonus_type: e.target.value })} className="crm-input">
                    <option value="">Sem bônus</option><option value="fixed">Fixo (¥)</option><option value="percentage">Percentual (%)</option>
                  </select>
                </div>
                <div>
                  <label className="crm-label">{salaryForm.bonus_type === "percentage" ? "Percentual (%)" : "Valor (¥)"}</label>
                  <input type="number" min="0" value={salaryForm.bonus_value} onChange={(e) => setSalaryForm({ ...salaryForm, bonus_value: e.target.value })}
                    className="crm-input" disabled={!salaryForm.bonus_type} />
                </div>
              </div>
            </div>

            {/* Work schedule */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: "var(--crm-text-muted)" }}>
                <Clock className="w-3 h-3 inline mr-1" />Horário de Trabalho
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="crm-label">Hora de Entrada</label>
                  <input type="time" value={salaryForm.work_start} onChange={(e) => setSalaryForm({ ...salaryForm, work_start: e.target.value })}
                    className="crm-input" data-testid="work-start" />
                </div>
                <div>
                  <label className="crm-label">Hora de Saída</label>
                  <input type="time" value={salaryForm.work_end} onChange={(e) => setSalaryForm({ ...salaryForm, work_end: e.target.value })}
                    className="crm-input" data-testid="work-end" />
                </div>
              </div>

              <div>
                <label className="crm-label">
                  <Calendar className="w-3 h-3 inline mr-1" />Dias de Trabalho
                </label>
                <div className="flex gap-2 mt-1">
                  {DAY_LABELS.map((label, idx) => {
                    const active = salaryForm.work_days.includes(idx);
                    return (
                      <button key={idx} type="button" onClick={() => toggleDay(idx)}
                        data-testid={`work-day-${idx}`}
                        className="w-11 h-11 rounded-xl text-xs font-bold transition-all"
                        style={{
                          background: active ? "var(--crm-accent)" : "var(--crm-input-bg)",
                          color: active ? "white" : "var(--crm-text-dim)",
                          border: `2px solid ${active ? "var(--crm-accent)" : "var(--crm-border)"}`,
                        }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 flex gap-3" style={{ borderTop: "1px solid var(--crm-border)" }}>
            <button type="submit" disabled={saving} className="crm-btn-primary text-sm">{saving ? "Salvando..." : "Salvar Configuração"}</button>
            <button type="button" onClick={() => setConfigId(null)} className="crm-btn-secondary text-sm">Cancelar</button>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="crm-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="crm-table">
            <thead><tr><th>Nome</th><th>Email</th><th>Cargo</th><th>Horário</th><th>Salário</th><th>Status</th><th className="text-right">Ações</th></tr></thead>
            <tbody>
              {operators.map((op) => (
                <tr key={op.id} data-testid={`operator-row-${op.id}`}
                  style={{ background: configId === op.id ? "var(--crm-accent-subtle)" : undefined }}>
                  <td className="primary">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: "var(--crm-accent-subtle)", color: "var(--crm-accent)" }}>
                        {op.name?.charAt(0)?.toUpperCase()}
                      </div>
                      {op.name}
                    </div>
                  </td>
                  <td>{op.email}</td>
                  <td><span className={roleBadge[op.role] || "crm-badge crm-badge-gray"}>{roleLabels[op.role] || op.role}</span></td>
                  <td style={{ color: "var(--crm-text-secondary)" }}>
                    {op.work_start && op.work_end ? `${op.work_start} - ${op.work_end}` : <span style={{ color: "var(--crm-text-dim)" }}>Não definido</span>}
                    {op.work_days?.length > 0 && (
                      <div className="flex gap-1 mt-1">
                        {op.work_days.map((d) => (
                          <span key={d} className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: "var(--crm-accent-subtle)", color: "var(--crm-accent)" }}>{DAY_LABELS[d]}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ color: "var(--crm-text)" }}>
                    {op.base_salary ? <span className="font-semibold">¥{Number(op.base_salary).toLocaleString()}</span> : <span style={{ color: "var(--crm-text-dim)" }}>—</span>}
                    {op.hourly_rate ? <span className="text-xs block" style={{ color: "var(--crm-text-muted)" }}>¥{Number(op.hourly_rate).toLocaleString()}/h</span> : null}
                  </td>
                  <td>
                    <span className={op.status === "active" ? "crm-badge crm-badge-green" : "crm-badge crm-badge-gray"}>
                      {op.status === "active" ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => startConfig(op)} className="p-2 rounded-lg transition-colors" style={{ color: "var(--crm-text-muted)" }} title="Configurar Salário/Horário">
                        <DollarSign className="w-4 h-4" />
                      </button>
                      <button onClick={() => startEdit(op)} className="p-2 rounded-lg transition-colors" style={{ color: "var(--crm-text-muted)" }} title="Editar">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => toggleStatus(op)} className="text-xs font-medium px-3 py-1.5 rounded-lg"
                        style={{ color: op.status === "active" ? "#ef4444" : "#10b981", background: op.status === "active" ? "rgba(239,68,68,0.06)" : "rgba(16,185,129,0.06)" }}>
                        {op.status === "active" ? "Desativar" : "Ativar"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
